import { FormEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import Card from "../../components/shared/Card/Card";
import { ButtonInput } from "../../components/shared/Inputs/ButtonInput";
import useNotification from "../../components/notifications/useNotifications";
import { usePageNameContext } from "../../contexts/PageTitleContext";
import { useAuth } from "../../contexts/AuthContext";
import useTickets from "../../data/useTickets";
import { ICreateTicketRequest, TicketPriority } from "../../models/Ticket";

export default function CreateTicket() {
  const navigate = useNavigate();
  const { setInfo } = usePageNameContext();
  const { addNotification } = useNotification();
  const { userId, customerId } = useAuth();
  const { createTicket, uploadAttachment } = useTickets();

  const [formData, setFormData] = useState<ICreateTicketRequest>({
    subject: "",
    description: "",
    priority: TicketPriority.Medium,
  });
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const maxAttachmentSizeBytes = 100 * 1024 * 1024;

  useEffect(() => {
    setInfo({
      name: "Create Support Ticket",
      desc: "Raise a new support request",
    });
  }, [setInfo]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!formData.subject.trim() || !formData.description.trim()) {
      addNotification({
        variant: "warn",
        primaryText: "Please enter a subject and description",
      });
      return;
    }

    const oversizedFile = selectedFiles.find((file) => file.size > maxAttachmentSizeBytes);
    if (oversizedFile) {
      addNotification({
        variant: "warn",
        primaryText: `${oversizedFile.name} exceeds the 100MB limit`,
      });
      return;
    }

    const payload: ICreateTicketRequest = {
      ...formData,
      customerId: customerId ?? undefined,
      createdByUserId: userId ?? undefined,
    };

    try {
      const created = await createTicket.mutateAsync(payload);
      const createdTicketId = created?.data?.id;

      if (createdTicketId && selectedFiles.length > 0) {
        for (const file of selectedFiles) {
          const fileData = new FormData();
          fileData.append("file", file);
          await uploadAttachment.mutateAsync({ ticketId: createdTicketId, data: fileData });
        }
      }

      addNotification({
        variant: "success",
        primaryText: "Ticket created successfully",
      });
      navigate("/tickets");
    } catch (error: any) {
      addNotification({
        variant: "error",
        primaryText: error?.message || "Failed to create ticket",
      });
    }
  };

  return (
    <Card containerClassName="mt-4">
      <form onSubmit={onSubmit} className="p-4 space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700">Subject</label>
          <input
            type="text"
            className="mt-1 w-full sm:text-sm"
            value={formData.subject}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, subject: e.target.value }))
            }
            maxLength={200}
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Priority</label>
          <select
            className="mt-1 w-full sm:text-sm"
            value={formData.priority}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                priority: Number(e.target.value) as TicketPriority,
              }))
            }
          >
            <option value={TicketPriority.Low}>Low</option>
            <option value={TicketPriority.Medium}>Medium</option>
            <option value={TicketPriority.High}>High</option>
            <option value={TicketPriority.Urgent}>Urgent</option>
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Description</label>
          <textarea
            className="mt-1 w-full sm:text-sm"
            rows={6}
            value={formData.description}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, description: e.target.value }))
            }
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700">Attachments</label>
          <input
            type="file"
            multiple
            className="mt-1 block w-full text-sm text-gray-700"
            onChange={(e) => setSelectedFiles(Array.from(e.target.files ?? []))}
          />
          <p className="mt-1 text-xs text-gray-500">Add files to include with this ticket (optional).</p>
          <p className="text-xs text-gray-500">Maximum file size: 100MB per attachment.</p>
          {selectedFiles.length > 0 ? (
            <ul className="mt-2 space-y-1 text-xs text-gray-600">
              {selectedFiles.map((file) => (
                <li key={`${file.name}-${file.lastModified}`}>- {file.name}</li>
              ))}
            </ul>
          ) : null}
        </div>

        <div className="flex justify-end gap-2">
          <ButtonInput
            label="Cancel"
            isSubmit={false}
            classes="bg-gray-200 !text-gray-700 hover:bg-gray-300"
            onClick={() => navigate("/tickets")}
          />
          <ButtonInput
            label="Create Ticket"
            isSubmit
            classes=""
            onClick={undefined}
            isLoading={createTicket.isLoading || uploadAttachment.isLoading}
          />
        </div>
      </form>
    </Card>
  );
}
